import * as React from 'react'

import { makeObservable, observable } from 'mobx'
import { classList } from 'react-classlist-helper'

import DynamicOverflowList from '~/client/src/shared/components/DynamicOverflowList/DynamicOverflowList'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import DeliveryVehicleTypesStore from '~/client/src/shared/stores/domain/DeliveryVehicleTypes.store'
import MaterialCategoryStore from '~/client/src/shared/stores/domain/MaterialCategories.store'
import MaterialsStore from '~/client/src/shared/stores/domain/Materials.store'
import OffloadingEquipmentsStore from '~/client/src/shared/stores/domain/OffloadingEquipments.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import InitialState from '~/client/src/shared/stores/InitialState'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'

import LocationAttributesStore from '../../stores/domain/LocationAttributes.store'
import ProjectsStore from '../../stores/domain/Projects.store'
import BaseExpandableWorkflowCardStore from './BaseExpandableWorkflowCard.store'
import ExpandableCardAttributeValues from './components/ExpandableCardAttributeValues'

import './BaseExpandableWorkflowCard.scss'

export interface IBaseWorkflowCardProps {
  className?: string
  measureSizeFn?: () => void

  eventStyles?: React.CSSProperties

  isCalendarTooltip?: boolean

  state?: InitialState
  companiesStore?: CompaniesStore
  projectDateStore?: ProjectDateStore
  locationAttributesStore?: LocationAttributesStore
  deliveryVehicleTypesStore?: DeliveryVehicleTypesStore
  offloadingEquipmentsStore?: OffloadingEquipmentsStore
  materialsStore?: MaterialsStore
  projectMembersStore?: ProjectMembersStore
  materialCategoryStore?: MaterialCategoryStore
  projectsStore?: ProjectsStore
}

export const workflowCardInjects = [
  'state',
  'companiesStore',
  'projectDateStore',
  'locationAttributesStore',
  'deliveryVehicleTypesStore',
  'offloadingEquipmentsStore',
  'materialsStore',
  'projectMembersStore',
  'materialCategoryStore',
  'projectsStore',
]

const MIN_ELEMENT_WIDTH = 50

export default class BaseExpandableWorkflowCard<
  T extends IBaseWorkflowCardProps,
> extends React.Component<T> {
  protected readonly store: BaseExpandableWorkflowCardStore
  @observable private measureSizeFn = null
  @observable private isCalendarTooltip: boolean = false

  public constructor(props: T) {
    super(props)

    this.store = new BaseExpandableWorkflowCardStore()
    this.measureSizeFn = props.measureSizeFn
    this.isCalendarTooltip = props.isCalendarTooltip
    makeObservable(this)
  }

  public componentDidMount() {
    if (this.props.measureSizeFn) {
      requestAnimationFrame(() => {
        this.props.measureSizeFn()
      })
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public componentDidUpdate(_prevProps: T) {
    this.measureSizeFn = this.props.measureSizeFn
    this.isCalendarTooltip = this.props.isCalendarTooltip
    this.props.measureSizeFn?.()
  }

  public render() {
    const { className, eventStyles, isCalendarTooltip } = this.props
    // onClick isn't on the base's generic props (subclasses add it) — read via a cast.
    const isClickable = !!(this.props as { onClick?: unknown }).onClick

    return (
      <div
        className={classList({
          'expandable-workflow-card row y-start no-flex': true,
          'tw:cursor-pointer': isClickable,
          'tw:cursor-default': !isClickable,
          [className]: !!className,
          pa10: !this.header,
          pb10: !!this.header,
        })}
        onClick={this.onClick}
        style={isCalendarTooltip && { color: eventStyles.color }}
      >
        {this.header ? (
          <div className="col">
            {this.header}
            <div className="px10 pt5 row y-start">{this.body}</div>
          </div>
        ) : (
          this.body
        )}
      </div>
    )
  }

  private get body(): JSX.Element {
    return (
      <>
        {this.icon}
        <div className="col ml4 relative overflow-hidden">
          {this.titleValues}
          {this.attributeValues}
        </div>
      </>
    )
  }

  protected get header(): JSX.Element {
    return null
  }

  protected get icon(): JSX.Element {
    return null
  }

  protected get titleValues(): JSX.Element {
    return null
  }

  protected get attributeElements() {
    return null
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected onClick = (ev: React.SyntheticEvent) => {
    return null
  }

  private get attributeValues(): JSX.Element {
    const { isExpanded, rowsCount, setExpandedState } = this.store

    return (
      <DynamicOverflowList
        items={this.attributeElements}
        rowsCount={rowsCount}
        minContainerWidthConstraint={MIN_ELEMENT_WIDTH}
        shouldRecalculateRowsCount={true}
        allowRecalculationForItems={true}
        shouldIgnoreScrollBarWidth={this.isCalendarTooltip}
      >
        {({ visibleElements, overflowElements, containerRefSetter }) => (
          <ExpandableCardAttributeValues
            isExpanded={!this.isCalendarTooltip && isExpanded}
            visibleElements={visibleElements}
            overflowElementsCount={
              !this.isCalendarTooltip && overflowElements?.length
            }
            containerRefSetter={containerRefSetter}
            setExpandedState={setExpandedState}
            measureSizeFn={this.measureSizeFn}
          />
        )}
      </DynamicOverflowList>
    )
  }
}
